import { useTranslation } from 'react-i18next'

import { Button } from '@atoms'

import { PageTitle } from '../components/common/PageTitle/PageTitle'
import { InvoiceDetailsContainer } from '../features/BillingPayments/containers/InvoiceDetailsContainer/InvoiceDetailsContainer'
import { payablesSummarySearchSchema } from '../schemas'
import useToastStore from '../stores/useToastStore/useToastStore'
import { useBillingApi } from '../hooks/useApi/useBillingApi/useBillingApi'
import { InvoiceBillsTableContainer } from '../features/BillingPayments/containers/InvoiceBillsTableContainer/InvoiceBillsTableContainer'
import { NavigateToPreviousLink } from '@/components/common/NavigateToPreviousLink/NavigateToPreviousLink'

type InvoicePageProps = {
    invoiceId: string
}

export const Invoice: React.FC<InvoicePageProps> = ({ invoiceId }) => {
    const { t } = useTranslation('translations')
    const { useGetInvoice } = useBillingApi()
    const { data } = useGetInvoice(invoiceId)
    const downloadButtonProps = useDownloadInvoiceButtonProps()
    return (
        <div className="grid h-full grid-rows-[auto_auto_1fr] gap-6 pb-6">
            <div className="space-y-3">
                <NavigateToPreviousLink
                    to="/billing-and-payments/payables-summary"
                    label={t('invoice.goBackToPayablesSummary')}
                    search={payablesSummarySearchSchema.parse({})}
                />
                <div className="flex flex-wrap items-center gap-y-3 lining-nums">
                    <PageTitle
                        label={`${t('invoice.title')} ${data?.invoiceNo ?? ''}`}
                        labelTestId="page-title-invoice"
                    />
                    <div className="ml-auto px-6">
                        {data?.invoiceDocId ? (
                            <DownloadInvoiceButton
                                invoiceDocId={data.invoiceDocId}
                            />
                        ) : (
                            <Button {...downloadButtonProps} disabled />
                        )}
                    </div>
                </div>
            </div>
            <div className="space-y-4 px-6">
                <div className="text-heading-6">
                    {t('invoice.details.title')}
                </div>
                <InvoiceDetailsContainer invoiceId={invoiceId} />
            </div>
            <div className="grid h-full min-h-[600px] grid-rows-[auto_1fr] overflow-hidden">
                <div className="text-heading-6 px-6">
                    {t('invoice_table.title')}
                </div>
                <div className="h-full overflow-hidden p-6">
                    <InvoiceBillsTableContainer invoiceId={invoiceId} />
                </div>
            </div>
        </div>
    )
}

const DownloadInvoiceButton: React.FC<{ invoiceDocId: number }> = ({
    invoiceDocId,
}) => {
    const addAlert = useToastStore((state) => state.addAlert)
    const { useGetDocument } = useBillingApi()
    const { t } = useTranslation('translations')
    const downloadButtonProps = useDownloadInvoiceButtonProps()

    const { refetch } = useGetDocument(invoiceDocId, {
        onError: () => {
            addAlert({
                variant: 'error',
                title: t('common.alerts.download_failed.title'),
                description: t('common.alerts.download_failed.description'),
            })
        },
    })

    return (
        <Button
            {...downloadButtonProps}
            onClick={() => {
                void refetch()
            }}
        />
    )
}

const useDownloadInvoiceButtonProps = () => {
    const { t } = useTranslation('translations')

    return {
        name: 'download-invoice',
        variant: 'outlinePrimary' as const,
        icon: 'file_download',
        size: 'xlarge' as const,
        iconSize: 'small' as const,
        children: `${t('invoice.downloadInvoiceButtonLabel')} (.pdf)`,
    }
}
